/**
 * CustomerDocumentsTab — documents linked to a single customer (TASK-01a).
 * Uses the documents service directly (scoped to one entity) so it stays
 * independent of the firm-wide document store.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Filter, Upload } from 'lucide-react'
import { returnCasesHttp } from '@/services/returnCases'
import { getAssessmentService } from '@/services/assessments'
import { getDocumentService } from '@/services/documents'
import { toast } from '@/store/toastStore'
import DocumentTable from '@/components/documents/DocumentTable'
import DocumentUploadModal from '@/components/documents/DocumentUploadModal'
import type { Document, DocumentCategory } from '@/types/document.types'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  labelForCategory,
  ASSESSMENT_YEAR_OPTIONS,
} from '@/types/document.types'
import type { IndividualCustomer } from '@/types/customer'
import { resolveRole } from '@/security/accessContract'
import { useAuth } from '@/contexts/AuthContext'

interface CustomerDocumentsTabProps {
  customer: IndividualCustomer
  /** When true (set by the Overview Quick Action), open the upload modal on mount. */
  autoCreate?: boolean
  /** Called once the auto-open has been handled, so the parent can clear its flag. */
  onAutoCreateHandled?: () => void
}

export default function CustomerDocumentsTab({
  customer,
  autoCreate,
  onAutoCreateHandled,
}: CustomerDocumentsTabProps) {
  const { user, hasPermission } = useAuth()
  const isAssistant = ['technical_assistant', 'research_assistant'].includes(
    resolveRole(user?.role).canonicalRole ?? ''
  )
  const canUpload = hasPermission('txh.document.upload')
  const canDownload = hasPermission('txh.document.download')
  const canVerify = hasPermission('txh.document.verify')
  const canDelete = hasPermission('txh.document.delete')
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [showUpload, setShowUpload] = useState(false)
  const [replacingDoc, setReplacingDoc] = useState<Document | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | 'all'>('all')
  const [selectedAY, setSelectedAY] = useState<string | 'all'>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const service = getDocumentService()
      const docs = await service.listForEntity('customer', customer.id)
      const groups = await Promise.allSettled([
        !isAssistant && hasPermission('txh.assessment.case.read')
          ? returnCasesHttp
              .list({ customerId: customer.id, pageSize: 100 })
              .then(cases =>
                Promise.all(
                  cases.items.map(item => service.listForEntity('assessment_case', item.id))
                )
              )
              .then(items => items.flat())
          : Promise.resolve([]),
        !isAssistant && hasPermission('txh.assessment.read')
          ? getAssessmentService()
              .listForCustomer(customer.id)
              .then(assessments =>
                Promise.all(
                  assessments.map(item => service.listForEntity('tax_assessment', item.id))
                )
              )
              .then(items => items.flat())
          : Promise.resolve([]),
      ])
      setDocuments([
        ...new Map(
          [
            ...docs,
            ...groups.flatMap(group => (group.status === 'fulfilled' ? group.value : [])),
          ].map(document => [document.id, document])
        ).values(),
      ])
      if (groups.some(group => group.status === 'rejected'))
        setLoadError(
          'Some assessment documents could not be loaded. Retry to refresh the full document list.'
        )
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Please try again.')
      toast.error(
        'Could not load documents',
        err instanceof Error ? err.message : 'Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }, [customer.id])

  useEffect(() => {
    load()
  }, [load])

  // Honor the Overview "Upload Document" Quick Action: open the upload modal once.
  useEffect(() => {
    if (autoCreate && canUpload) {
      setShowUpload(true)
      onAutoCreateHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate])

  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => {
      if (selectedCategory !== 'all' && doc.category !== selectedCategory) return false
      if (selectedAY !== 'all' && doc.assessmentYear !== selectedAY) return false
      return true
    })
  }, [documents, selectedCategory, selectedAY])

  const handleDownload = async (doc: Document) => {
    if (!canDownload) return
    try {
      const url = await getDocumentService().getDownloadUrl(doc.id)
      if (url) window.open(url, '_blank', 'noopener')
      else toast.success('Download started', doc.name)
    } catch (err) {
      toast.error('Download failed', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const handleVerify = async (doc: Document) => {
    if (!canVerify) return
    await getDocumentService().verify(doc.id, true)
    toast.success('Document verified', doc.name)
    load()
  }

  const handleReplace = (doc: Document) => {
    setReplacingDoc(doc)
    setShowUpload(true)
  }

  const handleDelete = async (doc: Document) => {
    if (!canDelete) return
    if (!window.confirm(`Delete "${doc.name}"?`)) return
    await getDocumentService().remove(doc.id)
    toast.success('Document deleted', doc.name)
    load()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-text-primary">Documents</h3>

        <div className="flex flex-wrap items-center gap-2">
          {/* Category filter */}
          <div className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-text-tertiary" />
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value as DocumentCategory | 'all')}
              className="input input-bordered text-sm !w-auto max-w-full"
              aria-label="Filter by Category"
            >
              <option value="all">All Categories</option>
              {DOCUMENT_CATEGORY_OPTIONS.map(c => (
                <option key={c} value={c}>
                  {labelForCategory(c)}
                </option>
              ))}
            </select>
          </div>

          {/* Assessment Year filter */}
          <select
            value={selectedAY}
            onChange={e => setSelectedAY(e.target.value)}
            className="input input-bordered text-sm !w-auto max-w-full"
            aria-label="Filter by Assessment Year"
          >
            <option value="all">All Assessment Years</option>
            {ASSESSMENT_YEAR_OPTIONS.map(ay => (
              <option key={ay} value={ay}>
                AY {ay}
              </option>
            ))}
          </select>

          {canUpload && (
            <button
              type="button"
              onClick={() => {
                setReplacingDoc(null)
                setShowUpload(true)
              }}
              className="btn btn-primary btn-sm gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div role="alert" className="card card-elevated p-4 text-sm text-red-600 dark:text-red-400">
          {loadError}{' '}
          <button type="button" className="btn btn-outline btn-sm" onClick={load}>
            Retry
          </button>
        </div>
      )}
      <DocumentTable
        documents={filteredDocuments}
        isLoading={loading}
        selectedIds={selectedIds}
        onToggleSelect={id =>
          setSelectedIds(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]))
        }
        onSelectAll={setSelectedIds}
        onDownload={canDownload ? handleDownload : undefined}
        onVerify={canVerify ? handleVerify : undefined}
        onReplace={canUpload ? handleReplace : undefined}
        onDelete={canDelete ? handleDelete : undefined}
      />

      {canUpload && (
        <DocumentUploadModal
          isOpen={showUpload}
          onClose={() => {
            setShowUpload(false)
            setReplacingDoc(null)
          }}
          replaceDocument={replacingDoc}
          linkedEntityType="customer"
          linkedEntityId={customer.id}
          onSubmit={async input => {
            if (!canUpload) return
            await getDocumentService().upload(input)
            toast.success(
              replacingDoc ? 'Document replaced (new version)' : 'Document uploaded',
              input.name
            )
            setShowUpload(false)
            setReplacingDoc(null)
            load()
          }}
        />
      )}
    </div>
  )
}
